import type { TeamMember } from '@qlk/shared';
import { ROLE_LABEL } from '@qlk/shared';
import { Users } from 'lucide-react';
import { useState } from 'react';

import { useGo2faCuaNguoiKhac } from '../../core/api.js';
import {
  Avatar,
  DataTable,
  EmptyState,
  HopThoai,
  HopXacNhan,
  MenuThaoTac,
  Nut,
  StatusBadge,
  ngayGioNgan,
  type CotBang,
  type MucThaoTac,
} from '../../../ui/index.js';
import { KhoiNguoiDaMoi } from './KhoiNguoiDaMoi.js';

/** "Đang online", "10 phút trước", "Hôm qua", "3 ngày trước". */
export function hoatDongGanNhat(iso: string | null): string {
  if (!iso) return 'Chưa đăng nhập';

  const phut = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (phut < 5) return 'Đang online';
  if (phut < 60) return `${String(phut)} phút trước`;

  const gio = Math.floor(phut / 60);
  if (gio < 24) return `${String(gio)} giờ trước`;

  const ngay = Math.floor(gio / 24);
  if (ngay === 1) return 'Hôm qua';
  return `${String(ngay)} ngày trước`;
}

export interface BangThanhVienProps {
  thanhVien: TeamMember[];
  /** Tên nhóm của từng người (`userId` → nhóm), lấy từ danh sách nhóm của kỳ. */
  nhomTheoNguoi: Map<string, string[]>;
  dangTai: boolean;
  coSua: boolean;
  onSua: (t: TeamMember) => void;
}

/**
 * Danh sách thành viên dạng **bảng gọn**.
 *
 * Bản cũ là lưới thẻ: mỗi người một thẻ cao 176px với avatar 44px, danh sách
 * kênh và hai chip — năm người đã chiếm hết một màn 1440×900, và hai câu hỏi
 * hằng ngày ("ai chưa bật 2FA", "ai lâu không vào") phải đọc từng thẻ mới trả
 * lời được. Bảng xếp cùng một thuộc tính thành một cột nên so sánh được bằng
 * mắt, và 12 người vừa một màn.
 *
 * Mỗi hàng **một** nút chính (Sửa) — mọi việc còn lại, kể cả khoá / lưu trữ /
 * xoá, nằm trong hộp thoại của nút đó như trước.
 */
export function BangThanhVien({
  thanhVien,
  nhomTheoNguoi,
  dangTai,
  coSua,
  onSua,
}: BangThanhVienProps) {
  const [idLoiMoi, setIdLoiMoi] = useState<string | null>(null);
  const hopLoiMoi = idLoiMoi === null ? null : (thanhVien.find((t) => t.id === idLoiMoi) ?? null);

  const [idGo2fa, setIdGo2fa] = useState<string | null>(null);
  const hopGo2fa = idGo2fa === null ? null : (thanhVien.find((t) => t.id === idGo2fa) ?? null);
  const go2fa = useGo2faCuaNguoiKhac();

  const cot: CotBang<TeamMember>[] = [
    {
      key: 'nguoi',
      header: 'Thành viên',
      render: (t) => (
        <div className="flex items-center gap-2.5">
          <Avatar name={t.name} color={t.avatarColor} />
          <div className="min-w-0">
            <p className="truncate font-medium text-chu">{t.name}</p>
            <p className="truncate text-phu text-chu-phu">{t.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'vai-tro',
      header: 'Vai trò',
      render: (t) => t.roles.map((r) => ROLE_LABEL[r]).join(' · '),
    },
    {
      key: 'nhom',
      header: 'Nhóm',
      render: (t) => nhomTheoNguoi.get(t.id)?.join(', '),
    },
    {
      key: 'kenh',
      header: 'Kênh',
      align: 'right',
      render: (t) =>
        t.channels.length === 0 ? null : (
          <span title={t.channels.map((k) => k.name).join(', ')}>{t.channels.length}</span>
        ),
    },
    {
      key: '2fa',
      header: '2FA',
      render: (t) => (
        <StatusBadge tone={t.twoFactorEnabled ? 'ok' : 'bad'} cham={false}>
          {t.twoFactorEnabled ? 'đã bật' : 'chưa bật'}
        </StatusBadge>
      ),
    },
    {
      key: 'trang-thai',
      header: 'Trạng thái',
      render: (t) =>
        t.status === 'disabled' ? (
          <StatusBadge
            tone="bad"
            {...(t.lockedAt
              ? {
                  className: 'cursor-help',
                }
              : {})}
          >
            <span
              title={
                t.lockedAt
                  ? `Khoá ${ngayGioNgan(t.lockedAt)}${t.lockedByName ? ` bởi ${t.lockedByName}` : ''}`
                  : undefined
              }
            >
              Đã khoá
            </span>
          </StatusBadge>
        ) : t.status === 'invited' ? (
          <StatusBadge tone="warn">Đã mời</StatusBadge>
        ) : (
          <StatusBadge tone="ok">Đang hoạt động</StatusBadge>
        ),
    },
    {
      key: 'hoat-dong',
      header: 'Lần hoạt động',
      render: (t) =>
        t.status === 'invited' ? 'Chưa nhận lời mời' : hoatDongGanNhat(t.lastActiveAt),
    },
  ];

  if (coSua) {
    cot.push({
      key: 'thao-tac',
      header: <span className="sr-only">Thao tác</span>,
      align: 'right',
      render: (t) => {
        const muc: MucThaoTac[] = [
          {
            nhan: 'Sửa & phân quyền',
            onChon: () => {
              onSua(t);
            },
          },
          {
            /* Chỉ có nghĩa với người chưa đặt mật khẩu. */
            nhan: 'Link đặt mật khẩu',
            tat: t.status !== 'invited',
            onChon: () => {
              setIdLoiMoi(t.id);
            },
          },
          {
            /*
             * Lớp khôi phục **thứ hai** khi một người mất cả điện thoại và mười
             * mã dự phòng (lớp một là mã dự phòng, lớp ba là CLI trên máy chủ —
             * xem `docs/deploy.md` mục 9).
             */
            nhan: 'Gỡ xác thực hai bước',
            tat: !t.twoFactorEnabled,
            onChon: () => {
              setIdGo2fa(t.id);
            },
          },
        ];

        return (
          <div className="flex items-center justify-end gap-1.5">
            <Nut
              kieu="phu"
              co="nho"
              onClick={() => {
                onSua(t);
              }}
            >
              Sửa
            </Nut>
            <MenuThaoTac muc={muc} aria-label={`Thao tác với ${t.name}`} />
          </div>
        );
      },
    });
  }

  return (
    <>
      <DataTable
        caption="Thành viên của công ty"
        columns={cot}
        rows={thanhVien}
        keyOf={(t) => t.id}
        dangTai={dangTai}
        tongSo={thanhVien.length}
        khiRong={
          <EmptyState
            icon={Users}
            title="Chưa có thành viên nào"
            description="Mời người đầu tiên vào hệ thống để bắt đầu giao kênh."
          />
        }
      />

      {hopLoiMoi && (
        <HopThoai
          title={`Lời mời của ${hopLoiMoi.name}`}
          onDong={() => {
            setIdLoiMoi(null);
          }}
        >
          <KhoiNguoiDaMoi thanhVien={hopLoiMoi} />
        </HopThoai>
      )}

      {hopGo2fa && (
        <HopXacNhan
          title={`Gỡ xác thực hai bước của ${hopGo2fa.name}`}
          nhanXacNhan="Gỡ"
          nguyHiem
          dangChay={go2fa.isPending}
          loi={go2fa.error?.message ?? null}
          onDong={() => {
            setIdGo2fa(null);
            go2fa.reset();
          }}
          onXacNhan={() => {
            go2fa.mutate(hopGo2fa.id, {
              onSuccess: () => {
                setIdGo2fa(null);
              },
            });
          }}
        >
          <p>
            Chỉ làm việc này khi {hopGo2fa.name} đã mất cả điện thoại và mười mã dự phòng, và bạn đã
            xác minh được đúng là họ.
          </p>
          <p className="mt-2">
            Gỡ xong: mọi phiên đang mở của họ bị đăng xuất, và lần vào sau họ phải bật lại từ đầu
            nếu vai trò của họ bắt buộc. Việc này được ghi vào nhật ký kèm tên bạn.
          </p>
        </HopXacNhan>
      )}
    </>
  );
}
